import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useCRMAuth } from '@/contexts/CRMAuthContext';
import CRMLayout from './CRMLayout';
import { toast } from 'sonner';
import {
  Plus, Search, Filter, CheckCircle2, Clock, AlertCircle, 
  Calendar, User, Building2, Loader2, MoreVertical, Edit, Trash2,
  Paperclip, Upload, Download, X, FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-800', icon: Clock },
  submitted: { label: 'Submitted', color: 'bg-purple-100 text-purple-800', icon: CheckCircle2 },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: AlertCircle },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-800', icon: AlertCircle }
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Medium', color: 'bg-yellow-100 text-yellow-700' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-700' },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-700' }
};

export default function CRMTasks() {
  const { token, isAdmin, user } = useCRMAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '', title: '', description: '', work_type_id: '',
    assigned_to: '', due_date: '', priority: 'medium'
  });
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [taskFiles, setTaskFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchTasks();
    fetchClients();
    fetchEmployees();
    fetchWorkTypes();
  }, [statusFilter]);

  const fetchTasks = async () => {
    try {
      let url = `${API}/api/crm/tasks/?`;
      if (statusFilter !== 'all') url += `status=${statusFilter}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTasks(data.tasks);
      }
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await fetch(`${API}/api/crm/clients/?status=active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClients(data.clients);
      }
    } catch (err) {
      // Silent fail - clients will show empty
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${API}/api/crm/users?role=employee`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEmployees(data.users);
      }
    } catch (err) {
      // Silent fail - employees will show empty
    }
  };

  const fetchWorkTypes = async () => {
    try {
      const res = await fetch(`${API}/api/crm/work-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWorkTypes(data.work_types);
      }
    } catch (err) {
      // Silent fail - work types will show empty
    }
  };

  const handleSaveTask = async () => {
    if (!formData.client_id || !formData.title || !formData.assigned_to) {
      toast.error('Please fill required fields');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API}/api/crm/tasks/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success('Task created!');
        setShowAddModal(false);
        setFormData({
          client_id: '', title: '', description: '', work_type_id: '',
          assigned_to: '', due_date: '', priority: 'medium'
        });
        fetchTasks();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create task');
      }
    } catch (err) {
      toast.error('Failed to create task');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const res = await fetch(`${API}/api/crm/tasks/${taskId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success('Status updated');
        fetchTasks();
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm('Delete this task?')) return;
    
    try {
      const res = await fetch(`${API}/api/crm/tasks/${taskId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Task deleted');
        fetchTasks();
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const openTaskDetail = async (task) => {
    setSelectedTask(task);
    setShowTaskDetail(true);
    // Fetch files for this task
    try {
      const res = await fetch(`${API}/api/crm/attachments/task/${task.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTaskFiles(data.files || []);
      }
    } catch (err) {
      setTaskFiles([]);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTask) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('task_id', selectedTask.id);

    try {
      const res = await fetch(`${API}/api/crm/attachments/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        toast.success('File uploaded!');
        // Refresh files
        const filesRes = await fetch(`${API}/api/crm/attachments/task/${selectedTask.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (filesRes.ok) {
          const data = await filesRes.json();
          setTaskFiles(data.files || []);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Upload failed');
      }
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      const res = await fetch(`${API}/api/crm/attachments/${fileId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('File deleted');
        setTaskFiles(prev => prev.filter(f => f.id !== fileId));
      }
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const filteredTasks = tasks.filter(t => 
    t.title?.toLowerCase().includes(search.toLowerCase()) ||
    t.client_name?.toLowerCase().includes(search.toLowerCase())
  );

  const groupedTasks = {
    pending: filteredTasks.filter(t => t.status === 'pending'),
    in_progress: filteredTasks.filter(t => t.status === 'in_progress'),
    submitted: filteredTasks.filter(t => t.status === 'submitted'),
    completed: filteredTasks.filter(t => ['completed', 'approved'].includes(t.status))
  };

  return (
    <CRMLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tasks</h1>
          <p className="text-slate-500">Manage and track work assignments</p>
        </div>
        {isAdmin() && (
          <Button onClick={() => setShowAddModal(true)} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-2" /> Create Task
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search tasks..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Kanban Board */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Pending Column */}
          <div className="bg-yellow-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-yellow-600" />
              <h3 className="font-semibold text-yellow-900">Pending</h3>
              <Badge className="bg-yellow-200 text-yellow-800">{groupedTasks.pending.length}</Badge>
            </div>
            <div className="space-y-3">
              {groupedTasks.pending.map(task => (
                <TaskCard key={task.id} task={task} onStatusChange={handleStatusChange} onDelete={handleDeleteTask} isAdmin={isAdmin()} onViewDetails={openTaskDetail} />
              ))}
              {groupedTasks.pending.length === 0 && (
                <p className="text-sm text-yellow-600 text-center py-4">No pending tasks</p>
              )}
            </div>
          </div>

          {/* In Progress Column */}
          <div className="bg-blue-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-blue-900">In Progress</h3>
              <Badge className="bg-blue-200 text-blue-800">{groupedTasks.in_progress.length}</Badge>
            </div>
            <div className="space-y-3">
              {groupedTasks.in_progress.map(task => (
                <TaskCard key={task.id} task={task} onStatusChange={handleStatusChange} onDelete={handleDeleteTask} isAdmin={isAdmin()} onViewDetails={openTaskDetail} />
              ))}
              {groupedTasks.in_progress.length === 0 && (
                <p className="text-sm text-blue-600 text-center py-4">No tasks in progress</p>
              )}
            </div>
          </div>

          {/* Submitted Column */}
          <div className="bg-purple-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-purple-900">Submitted</h3>
              <Badge className="bg-purple-200 text-purple-800">{groupedTasks.submitted.length}</Badge>
            </div>
            <div className="space-y-3">
              {groupedTasks.submitted.map(task => (
                <TaskCard key={task.id} task={task} onStatusChange={handleStatusChange} onDelete={handleDeleteTask} isAdmin={isAdmin()} onViewDetails={openTaskDetail} />
              ))}
              {groupedTasks.submitted.length === 0 && (
                <p className="text-sm text-purple-600 text-center py-4">No submitted tasks</p>
              )}
            </div>
          </div>

          {/* Completed Column */}
          <div className="bg-emerald-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <h3 className="font-semibold text-emerald-900">Completed</h3>
              <Badge className="bg-emerald-200 text-emerald-800">{groupedTasks.completed.length}</Badge>
            </div>
            <div className="space-y-3">
              {groupedTasks.completed.map(task => (
                <TaskCard key={task.id} task={task} onStatusChange={handleStatusChange} onDelete={handleDeleteTask} isAdmin={isAdmin()} onViewDetails={openTaskDetail} />
              ))}
              {groupedTasks.completed.length === 0 && (
                <p className="text-sm text-emerald-600 text-center py-4">No completed tasks</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Task Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Client *</label>
              <Select value={formData.client_id} onValueChange={(v) => setFormData({...formData, client_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Task Title *</label>
              <Input
                placeholder="e.g., Create 5 Instagram Posts"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Work Type</label>
              <Select value={formData.work_type_id} onValueChange={(v) => setFormData({...formData, work_type_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select work type" />
                </SelectTrigger>
                <SelectContent>
                  {workTypes.map(wt => (
                    <SelectItem key={wt.id} value={wt.id}>{wt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Assign To *</label>
              <Select value={formData.assigned_to} onValueChange={(v) => setFormData({...formData, assigned_to: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Due Date</label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Priority</label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Description</label>
              <Textarea
                placeholder="Task details..."
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleSaveTask} disabled={saving}>
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : 'Create Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Detail Modal with Files */}
      <Dialog open={showTaskDetail} onOpenChange={setShowTaskDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="w-5 h-5" />
              Task Details & Attachments
            </DialogTitle>
          </DialogHeader>
          
          {selectedTask && (
            <div className="space-y-6 py-4">
              {/* Task Info */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg text-slate-900">{selectedTask.title}</h3>
                {selectedTask.description && (
                  <p className="text-sm text-slate-600">{selectedTask.description}</p>
                )}
                <div className="flex flex-wrap gap-2">
                  <Badge className={STATUS_CONFIG[selectedTask.status]?.color}>
                    {STATUS_CONFIG[selectedTask.status]?.label}
                  </Badge>
                  <Badge className={PRIORITY_CONFIG[selectedTask.priority]?.color}>
                    {PRIORITY_CONFIG[selectedTask.priority]?.label}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-slate-500">Client:</span> {selectedTask.client_name}</div>
                  <div><span className="text-slate-500">Assigned to:</span> {selectedTask.assigned_to_name}</div>
                  {selectedTask.due_date && (
                    <div><span className="text-slate-500">Due:</span> {new Date(selectedTask.due_date).toLocaleDateString()}</div>
                  )}
                </div>
              </div>

              {/* File Upload */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-slate-900">Attachments ({taskFiles.length})</h4>
                  <label className="cursor-pointer">
                    <input 
                      type="file" 
                      className="hidden" 
                      onChange={handleFileUpload}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt"
                    />
                    <Button size="sm" variant="outline" disabled={uploading} asChild>
                      <span>
                        {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                        Upload File
                      </span>
                    </Button>
                  </label>
                </div>

                {taskFiles.length === 0 ? (
                  <div className="text-center py-6 bg-slate-50 rounded-lg">
                    <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No files attached</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {taskFiles.map(file => (
                      <div key={file.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-indigo-500" />
                          <div>
                            <p className="text-sm font-medium text-slate-900">{file.filename}</p>
                            <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <a 
                            href={`${API}/api/crm/attachments/${file.id}/download`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Download className="w-4 h-4" />
                            </Button>
                          </a>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-red-500"
                            onClick={() => handleDeleteFile(file.id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDetail(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CRMLayout>
  );
}

// Task Card Component
function TaskCard({ task, onStatusChange, onDelete, isAdmin, onViewDetails }) {
  const priorityConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
  
  return (
    <Card className="p-3 bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => onViewDetails(task)}>
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-medium text-slate-900 text-sm line-clamp-2">{task.title}</h4>
        <Badge className={`text-xs ${priorityConfig.color}`}>{priorityConfig.label}</Badge>
      </div>
      
      <div className="space-y-1 mb-3">
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <Building2 className="w-3 h-3" />
          <span className="truncate">{task.client_name}</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <User className="w-3 h-3" />
          <span>{task.assigned_to_name}</span>
        </div>
        {task.due_date && (
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <Calendar className="w-3 h-3" />
            <span>{new Date(task.due_date).toLocaleDateString()}</span>
          </div>
        )}
      </div>
      
      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <Select value={task.status} onValueChange={(v) => onStatusChange(task.id, v)}>
          <SelectTrigger className="h-7 text-xs flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        {isAdmin && (
          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => onDelete(task.id)}>
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
      </div>
    </Card>
  );
}
